import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Link,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTranslation } from '../hooks/useTranslation';
import { useCheckout } from '../contexts/CheckoutContext';
import { useAuth } from '../contexts/AuthContext';
import { CheckoutShippingAddress } from '../components/CheckoutShippingAddress';
import {
  requiresShippingAddress,
  isShippingAddressValid,
  normalizeShippingAddress,
  hasShippingAddressContent,
  defaultRecipientFullName,
  type ShippingAddress,
} from '../utils/checkoutShipping';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import { Link as RouterLink } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import api from '../utils/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
}));

const EmptyCartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const CartIcon = styled(ShoppingCartIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const TotalBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.light,
  borderRadius: theme.spacing(1),
  margin: theme.spacing(2, 0),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    textAlign: 'inherit',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    textAlign: 'inherit',
  },
  '& .MuiTableBody-root .MuiTableCell-root:last-child': {
    textAlign: 'center',
  },
  '& .MuiTableBody-root .MuiTableCell-root:nth-last-child(-n+3)': {
    textAlign: 'right',
  },
}));

const ProductImage = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: theme.spacing(1),
  objectFit: 'cover',
}));

const QuantityControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

const QuantityText = styled(Typography)(() => ({
  minWidth: '2ch',
  textAlign: 'center',
}));

const emptyShippingAddress = (): ShippingAddress => ({
  fullName: '',
  city: '',
  street: '',
  streetNumber: '',
  flat: '',
  notes: '',
  phone: '',
});

export const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { user } = useAuth();
  const {
    items,
    sessionId,
    savedShippingAddress,
    removeFromCheckout,
    clearCheckout,
    updateQuantity,
    saveShippingAddress,
    fetchCheckoutSession,
  } = useCheckout();
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>(emptyShippingAddress);
  const [showShippingErrors, setShowShippingErrors] = useState(false);
  const [confirmAddressOpen, setConfirmAddressOpen] = useState(false);

  const needsShipping = requiresShippingAddress(items);
  const shippingValid = !needsShipping || isShippingAddressValid(shippingAddress);
  const isRTL = currentLang === 'he';

  React.useEffect(() => {
    fetchCheckoutSession();
  }, [fetchCheckoutSession]);

  // Restore saved address and pre-fill empty fields from profile (phone can load after name)
  React.useEffect(() => {
    if (!sessionId || !needsShipping) return;

    const defaultName = defaultRecipientFullName(user?.firstName, user?.lastName);
    const profilePhone = user?.phoneNumber?.trim() ?? '';
    const saved = savedShippingAddress
      ? normalizeShippingAddress(savedShippingAddress, defaultName)
      : null;

    setShippingAddress((prev) => {
      const next: ShippingAddress = {
        fullName: prev.fullName || saved?.fullName || defaultName,
        city: prev.city || saved?.city || '',
        street: prev.street || saved?.street || '',
        streetNumber: prev.streetNumber || saved?.streetNumber || '',
        flat: prev.flat || saved?.flat || '',
        notes: prev.notes || saved?.notes || '',
        phone: prev.phone || saved?.phone || profilePhone,
      };

      if (
        next.fullName === prev.fullName &&
        next.city === prev.city &&
        next.street === prev.street &&
        next.streetNumber === prev.streetNumber &&
        next.flat === prev.flat &&
        next.notes === prev.notes &&
        next.phone === prev.phone
      ) {
        return prev;
      }

      return next;
    });
  }, [
    sessionId,
    needsShipping,
    savedShippingAddress,
    user?.firstName,
    user?.lastName,
    user?.phoneNumber,
  ]);

  const debouncedSaveShipping = useDebouncedCallback(
    (address: ShippingAddress) => {
      if (!sessionId || !needsShipping || !hasShippingAddressContent(address)) return;
      saveShippingAddress(address);
    },
    500
  );

  const handleShippingAddressChange = useCallback(
    (address: ShippingAddress) => {
      setShippingAddress(address);
      debouncedSaveShipping(address);
    },
    [debouncedSaveShipping]
  );

  // Initialize local quantities when items change
  React.useEffect(() => {
    const quantities = items.reduce((acc, item) => ({
      ...acc,
      [item.item]: item.quantity
    }), {});
    setLocalQuantities(quantities);
  }, [items]);

  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);

  const debouncedApiCall = useDebouncedCallback(
    async (itemId: string) => {
      try {
        setIsUpdating(prev => ({ ...prev, [itemId]: true }));
        const finalQuantity = localQuantities[itemId];
        await updateQuantity(itemId, finalQuantity);
      } catch (error) {
        console.error('Error updating quantity:', error);
        // Revert local quantity on error
        setLocalQuantities(prev => ({
          ...prev,
          [itemId]: items.find(item => item.item === itemId)?.quantity || 1
        }));
      } finally {
        setIsUpdating(prev => ({ ...prev, [itemId]: false }));
      }
    },
    500 // 500ms delay
  );

  const handleQuantityChange = useCallback((itemId: string, change: number) => {
    // Update local state immediately
    setLocalQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, (prev[itemId] || 1) + change)
    }));
    // Debounce the API call with the final quantity
    debouncedApiCall(itemId);
  }, [debouncedApiCall]);

  const proceedToPayment = async () => {
    setIsProcessingPayment(true);

    try {
      const response = await api.post('/payments/form', {
        checkoutSessionId: sessionId,
        language: currentLang === 'he' ? 'he' : 'en',
        ...(needsShipping ? { shippingAddress } : {}),
      });

      if (response.data.success && response.data.data.paymentUrl) {
        window.location.href = response.data.data.paymentUrl;
      } else {
        throw new Error('Failed to create payment form');
      }
    } catch (error: any) {
      console.error('Payment initiation error:', error);
      const errorMessage =
        error.response?.data?.error || 'Failed to initiate payment. Please try again.';
      alert(errorMessage);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleProceedClick = async () => {
    if (!termsAccepted) return;

    if (needsShipping && !isShippingAddressValid(shippingAddress)) {
      setShowShippingErrors(true);
      return;
    }

    if (needsShipping && hasShippingAddressContent(shippingAddress)) {
      debouncedSaveShipping.flush();
      await saveShippingAddress(normalizeShippingAddress(shippingAddress));
      setConfirmAddressOpen(true);
      return;
    }

    await proceedToPayment();
  };

  const handleConfirmAddress = async () => {
    setConfirmAddressOpen(false);
    await proceedToPayment();
  };

  const handleCancelAddressConfirm = () => {
    setConfirmAddressOpen(false);
  };

  const handleClearCheckout = () => {
    clearCheckout();
    navigate('/');
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return t('checkout.productType.tutorial');
      case 'Workshop':
        return t('checkout.productType.workshop');
      case 'Item':
        return t('checkout.productType.item');
      case 'File':
        return t('checkout.productType.file');
      default:
        return type;
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <StyledPaper>
        <Box sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {t('checkout.title')}
          </Typography>
          
          {items.length === 0 ? (
            <EmptyCartContainer>
              <CartIcon />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('checkout.empty')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('checkout.emptySubtitle')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
                sx={{ borderRadius: 2 }}
              >
                {t('checkout.continueShopping')}
              </Button>
            </EmptyCartContainer>
          ) : (
            <>
              <TableContainer>
                <StyledTable>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('checkout.type')}</TableCell>
                      <TableCell>{t('checkout.image')}</TableCell>
                      <TableCell>{t('checkout.productTitle')}</TableCell>
                      <TableCell>{t('checkout.price')}</TableCell>
                      <TableCell>{t('checkout.quantity')}</TableCell>
                      <TableCell>{t('checkout.total')}</TableCell>
                      <TableCell>{t('checkout.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.item}>
                        <TableCell>
                          <Chip 
                            label={getProductTypeLabel(item.productType)} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <ProductImage
                            src={item.productDetails?.image || '/placeholder-image.jpg'}
                            alt={item.productDetails?.title || 'Product image'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.productDetails?.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.price} {t('checkout.currency')}
                        </TableCell>
                        <TableCell>
                          {item.productType === 'Item' ? (
                            <QuantityControls>
                              <QuantityButton
                                size="small"
                                onClick={() => handleQuantityChange(item.item, -1)}
                                disabled={localQuantities[item.item] <= 1 || isUpdating[item.item]}
                              >
                                <RemoveIcon fontSize="small" />
                              </QuantityButton>
                              <QuantityText variant="body2">
                                {localQuantities[item.item] || item.quantity}
                              </QuantityText>
                              <QuantityButton
                                size="small"
                                onClick={() => handleQuantityChange(item.item, 1)}
                                disabled={isUpdating[item.item]}
                              >
                                <AddIcon fontSize="small" />
                              </QuantityButton>
                            </QuantityControls>
                          ) : (
                            <Typography variant="body2">
                              {item.quantity}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(item.price * item.quantity).toFixed(2)} {t('checkout.currency')}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeFromCheckout(item.item)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
              
              <TotalBox>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" color="primary">
                    {t('checkout.total')}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {totalPrice} {t('checkout.currency')}
                  </Typography>
                </Stack>
              </TotalBox>

              {needsShipping && (
                <CheckoutShippingAddress
                  value={shippingAddress}
                  onChange={handleShippingAddressChange}
                  showErrors={showShippingErrors}
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography component="span">
                      {t('checkout.terms.accept')}
                    </Typography>
                    {' '}
                    <Link
                      component={RouterLink}
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('checkout.terms.view')}
                    </Link>
                  </Box>
                }
                sx={{ mt: 2 }}
              />

              <Stack direction="row" gap={4} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClearCheckout}
                  fullWidth
                  sx={{ borderRadius: 2 }}
                  disabled={isProcessingPayment}
                >
                  {t('checkout.clear')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleProceedClick}
                  fullWidth
                  sx={{ borderRadius: 2 }}
                  disabled={!termsAccepted || isProcessingPayment || !shippingValid}
                  startIcon={isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isProcessingPayment ? t('checkout.processing') || 'Processing...' : t('checkout.proceed')}
                </Button>
              </Stack>

              <Dialog
                open={confirmAddressOpen}
                onClose={handleCancelAddressConfirm}
                maxWidth="sm"
                fullWidth
                sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
              >
                <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  {t('checkout.shipping.confirm.title')}
                </DialogTitle>
                <DialogContent sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                  <DialogContentText sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
                    {t('checkout.shipping.confirm.message')}
                  </DialogContentText>
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <strong>{t('checkout.shipping.fullName')}:</strong> {shippingAddress.fullName}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <strong>{t('checkout.shipping.phone')}:</strong> {shippingAddress.phone}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <strong>{t('checkout.shipping.city')}:</strong> {shippingAddress.city}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <strong>{t('checkout.shipping.street')}:</strong> {shippingAddress.street}{' '}
                      {shippingAddress.streetNumber}
                      {shippingAddress.flat
                        ? `, ${t('checkout.shipping.flat')} ${shippingAddress.flat}`
                        : ''}
                    </Typography>
                    {shippingAddress.notes?.trim() && (
                      <Typography variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <strong>{t('checkout.shipping.notes')}:</strong> {shippingAddress.notes}
                      </Typography>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={handleCancelAddressConfirm} disabled={isProcessingPayment}>
                    {t('checkout.shipping.confirm.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmAddress}
                    disabled={isProcessingPayment}
                    startIcon={
                      isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : null
                    }
                  >
                    {t('checkout.shipping.confirm.confirm')}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      </StyledPaper>
    </Container>
  );
};